import React, { useState } from 'react';
import { api, ApiUser } from '../services/api';
import { User, LogOut, CheckCircle2, Lock, X } from 'lucide-react';

interface AccountDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: ApiUser | null;
  onAuthChange: () => void;
}

export const AccountDrawer: React.FC<AccountDrawerProps> = ({
  isOpen,
  onClose,
  currentUser,
  onAuthChange,
}) => {
  const [authMode, setAuthMode] = useState<'LOGIN' | 'REGISTER'>('LOGIN');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);
    setIsLoading(true);

    try {
      if (authMode === 'REGISTER') {
        await api.auth.register(email, password, displayName);
        setSuccessMsg('Account created successfully!');
      } else {
        await api.auth.login(email, password);
        setSuccessMsg('Logged in successfully!');
      }
      onAuthChange();
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err: any) {
      setErrorMsg(err.message || 'Authentication failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.auth.logout();
      onAuthChange();
      onClose();
    } catch {
      // Safe fallback
    }
  };

  const hasIcePack = currentUser?.entitlements.includes('ICE_PACK') || currentUser?.entitlements.includes('ALL_ACCESS');
  const hasEvPack = currentUser?.entitlements.includes('EV_PACK') || currentUser?.entitlements.includes('ALL_ACCESS');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-xl p-4 select-none">
      <div className="relative w-full max-w-lg bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-4">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-cyan-400" />
            <h3 className="font-display font-black text-white text-lg tracking-wider uppercase">
              {currentUser ? 'MY ACCOUNT' : 'ACCOUNT ACCESS'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-xl bg-slate-800 text-slate-400 hover:text-white cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* LOGGED IN VIEW */}
        {currentUser ? (
          <div className="flex flex-col gap-4">
            {/* User Badge */}
            <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-white text-base">
                  {currentUser.displayName}
                </div>
                <div className="text-xs font-mono text-slate-400">
                  {currentUser.email}
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-[10px] font-mono font-bold bg-cyan-950 text-cyan-400 border border-cyan-500/40">
                {currentUser.role}
              </span>
            </div>

            {/* My Sounds & Entitlements */}
            <div>
              <div className="text-[11px] font-mono tracking-widest text-slate-400 uppercase mb-2">
                MY SOUND ENTITLEMENTS
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 flex flex-col justify-between">
                  <div className="text-xs font-bold font-display text-white mb-1">
                    ICE SOUND PACK
                  </div>
                  {hasIcePack ? (
                    <span className="text-[10px] font-mono text-emerald-400 font-bold flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> UNLOCKED
                    </span>
                  ) : (
                    <span className="text-[10px] font-mono text-amber-400 font-bold flex items-center gap-1">
                      <Lock className="w-3 h-3" /> LOCKED ($3.99)
                    </span>
                  )}
                </div>

                <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 flex flex-col justify-between">
                  <div className="text-xs font-bold font-display text-white mb-1">
                    EV SOUND PACK
                  </div>
                  {hasEvPack ? (
                    <span className="text-[10px] font-mono text-emerald-400 font-bold flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> UNLOCKED
                    </span>
                  ) : (
                    <span className="text-[10px] font-mono text-amber-400 font-bold flex items-center gap-1">
                      <Lock className="w-3 h-3" /> LOCKED ($3.99)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Log Out Button */}
            <button
              type="button"
              onClick={handleLogout}
              className="mt-2 w-full py-3 rounded-xl bg-slate-800 hover:bg-slate-750 text-rose-400 font-mono font-bold text-xs uppercase flex items-center justify-center gap-2 cursor-pointer"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        ) : (
          /* GUEST / AUTH FORM VIEW */
          <div>
            {/* Guest Banner */}
            <div className="p-3.5 rounded-2xl bg-cyan-950/40 border border-cyan-500/30 mb-4 text-xs text-slate-300 font-sans leading-relaxed">
              <span className="font-bold text-cyan-300 block mb-0.5">Enjoying REVIVE?</span>
              Create a free account to save your sound preferences and access your sound packs across all devices.
            </div>

            {/* Login / Register Tab Toggle */}
            <div className="grid grid-cols-2 gap-2 p-1 bg-slate-950 rounded-xl border border-slate-800 mb-4">
              <button
                type="button"
                onClick={() => setAuthMode('LOGIN')}
                className={`py-2 rounded-lg font-mono font-bold text-xs uppercase transition-all cursor-pointer ${
                  authMode === 'LOGIN' ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => setAuthMode('REGISTER')}
                className={`py-2 rounded-lg font-mono font-bold text-xs uppercase transition-all cursor-pointer ${
                  authMode === 'REGISTER' ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Create Account
              </button>
            </div>

            {errorMsg && (
              <div className="p-3 rounded-xl bg-rose-950/80 border border-rose-500/50 text-[11px] font-mono text-rose-300 mb-3">
                {errorMsg}
              </div>
            )}
            {successMsg && (
              <div className="p-3 rounded-xl bg-emerald-950/80 border border-emerald-500/50 text-[11px] font-mono text-emerald-300 mb-3">
                {successMsg}
              </div>
            )}

            <form onSubmit={handleAuthSubmit} className="flex flex-col gap-3">
              {authMode === 'REGISTER' && (
                <div>
                  <label className="block text-[10px] font-mono uppercase text-slate-400 mb-1">
                    Display Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="e.g. SpeedMaster"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs font-mono focus:border-cyan-500 focus:outline-none"
                  />
                </div>
              )}

              <div>
                <label className="block text-[10px] font-mono uppercase text-slate-400 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="driver@example.com"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs font-mono focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase text-slate-400 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs font-mono focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="mt-2 w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-display font-black text-xs uppercase tracking-wider shadow-lg hover:brightness-110 active:scale-[0.99] cursor-pointer"
              >
                {isLoading ? 'PROCESSING...' : authMode === 'LOGIN' ? 'SIGN IN' : 'CREATE FREE ACCOUNT'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
